<script setup lang="ts">
import type { JyunkaiItemProps } from '@/types/appTypes'

const props = withDefaults(defineProps<JyunkaiItemProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()

let tEyeCatch = null // アイキャッチ情報
let aEventInfo = null // イベント情報
let sOfficialLinkUrl = null // 公式サイトURL

// 巡回展から表示カラム作成
if (!tEyeCatch) {
  tEyeCatch = app.$getEyeCatch(props.item, 's')
} // アイキャッチ情報
if (!sOfficialLinkUrl && props.item.custom && props.item.custom.official_site) {
  // 公式サイトURL
  sOfficialLinkUrl = props.item.custom.official_site
}

// イベント情報作成
if (!aEventInfo) {
  aEventInfo = []

  // 親コンポーネントから設定が無ければ関連記事からイベント取得
  const eventItems = app.$getArticleRelationsMulti(props.item, 'event', null)

  if (eventItems) {
    for (let i = 0; i < eventItems.length; i++) {
      // イベント情報追加
      aEventInfo.push({
        eventItem: eventItems[i], // イベントアイテム
        sEventArticleSlug: eventItems[i].article_slug, // イベントスラッグ
        sPrefecture: storeIndex.getPrefName(eventItems[i].custom.custom_museum_prefecture), // 都道府県
        sVenue: app.$getEventVenue(eventItems[i], true), // 会場
        sMuseumArticleSlug: app.$getEventMuseumArticleSlug(eventItems[i]), // ミュージアムスラッグ
        sPeriodLabel: app.$getEventPeriodLabel(eventItems[i]), // 会期
        termFrom: eventItems[i].term_from, // 会期開始日付
      })
    }

    // 開始日付でソート
    aEventInfo = app.$sortArticleTermFrom(aEventInfo, false)
  }
}
</script>

<template>
  <div class="c-jyunkaiItem">
    <div
      v-if="tEyeCatch"
      class="c-jyunkaiItem_imageBox"
    >
      <div>
        <nuxt-link
          class="u-hoverZoom"
          v-bind:to="`/jyunkai/${item.article_slug}`"
        >
          <CommonImage
            class="c-jyunkaiItem_imageBox_image"
            v-bind:src="tEyeCatch.oImg"
            v-bind:alt="item.title"
            v-bind:position="app.$device.isMobile ? 'center' : 'top center'"
            v-bind:height="app.$device.isMobile ? '280' : ''"
            object-fit="contain"
            is-hover-effect
            error-src="/images/noimage.svg"
          />
        </nuxt-link>
      </div>
      <div
        v-if="tEyeCatch.sCaption"
        class="c-jyunkaiItem_imageBox_caption"
      >
        {{ tEyeCatch.sCaption }}
      </div>
    </div>

    <div class="c-jyunkaiItem_data">
      <div class="c-jyunkaiItem_data_title">
        <nuxt-link
          class="c-clampLines is-clampline2"
          v-bind:to="`/jyunkai/${item.article_slug}`"
        >
          {{ item.title }}
        </nuxt-link>
      </div>
      <div
        v-for="(r, index) in aEventInfo"
        v-bind:key="index"
        class="c-jyunkaiItem_data_detail"
      >
        <div class="c-jyunkaiItem_data_detail_city">
          {{ r.sPrefecture }}
        </div>
        <div class="c-jyunkaiItem_data_detail_info">
          <div class="c-jyunkaiItem_data_detail_info_first">
            <span class="c-jyunkaiItem_data_detail_info_first_status">
              {{ r.sPeriodLabel }}
            </span>
            <span class="c-jyunkaiItem_data_detail_info_first_museum">
              <nuxt-link
                class="c-jyunkaiItem_data_detail_info_first_museum_label"
                v-bind:to="`/museum/${r.sMuseumArticleSlug}`"
              >
                {{ r.sVenue }}
              </nuxt-link>
            </span>
          </div>
          <div class="c-jyunkaiItem_data_detail_info_second">
            <PeriodRemain
              v-if="r.eventItem"
              v-bind:term-from="r.eventItem.term_from"
              v-bind:term-to="r.eventItem.term_to"
              v-bind:hide-day-count="r.eventItem.day_count_display !== 1"
              small
            />
            <nuxt-link
              class="c-jyunkaiItem_data_detail_info_second_link"
              v-bind:to="`/event/${r.sEventArticleSlug}`"
            >
              展覧会の詳細はこちら
            </nuxt-link>
          </div>
        </div>
      </div>
      <div
        v-if="sOfficialLinkUrl"
        class="c-jyunkaiItem_data_website"
      >
        <span class="c-jyunkaiItem_data_website_label">
          公式サイト
        </span>
        <span class="c-jyunkaiItem_data_website_url">
          <a
            v-bind:href="sOfficialLinkUrl"
            target="_blank"
          >
            {{ sOfficialLinkUrl }}
          </a>
        </span>
      </div>
      <div class="c-jyunkaiItem_data_link">
        <ArrowLink
          v-bind:to="`/jyunkai/${item.article_slug}`"
          label="詳しい情報を見る"
          bold
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-jyunkaiItem {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid rgb(0, 0, 0, 0.12);
  font-family: var(--g_font_family_NotoSansCJKjp);

  @include responsive.mq_down(sp) {
    display: block;
    margin: 30px auto 0;
  }

  &_imageBox {
    width: 150px;

    @include responsive.mq_down(sp) {
      width: 100%;
    }

    &_image {
      flex-shrink: 0;
      width: 150px;
      max-height: 212px;

      @include responsive.mq_down(sp) {
        width: 100%;
        max-height: none;
      }
    }

    &_caption {
      margin-top: 5px;
      color: var(--color_text_base);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 12px;
      line-height: 2;
    }
  }

  &_data {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-left: 30px;

    @include responsive.mq_down(sp) {
      width: 100%;
      margin-top: 20px;
      margin-left: 0;
    }

    &_title {
      font-size: 18px;
      font-weight: bold;
      word-spacing: 0;

      a {
        color: #252525;
        font-weight: bold;
      }
    }

    &_detail {
      display: flex;
      align-items: flex-start;
      margin-top: 5px;

      &_city {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 60px;
        height: 60px;
        margin-top: 6px;
        background-color: var(--color_background_whiteblue);
        color: #7d7d7d;
        font-size: 13px;
        font-weight: 400;
        line-height: 33px;
        white-space: nowrap;
      }

      &_info {
        flex: 1;
        margin-top: 6px;
        margin-left: 10px;

        &_first {
          margin-bottom: 2px;

          &_status {
            display: block;
            font-size: 12px;
          }

          &_museum {
            display: block;
            margin: 5px 0;
            color: var(--color_primary_link);
            font-size: 14px;

            &_label {
              flex-basis: 100%;
              max-width: 100%;
              margin: 5px 0;
              color: var(--color_primary_link);
              font-size: 14px;
            }
          }
        }

        &_second {
          display: flex;
          flex-wrap: wrap;
          margin-bottom: 2px;

          &_link {
            flex-basis: 100%;
            max-width: 100%;
            margin: 5px 0;
            color: var(--color_primary_link);
            font-size: 12px;
          }
        }
      }
    }

    &_website {
      margin-top: 12px;

      @include responsive.mq_down(sp) {
        margin-top: 22px;
      }

      &_label {
        font-size: 14px;
        font-weight: bold;
      }

      &_url {
        margin-left: 10px;
        font-size: 14px;
      }
    }

    &_link {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    }
  }

  &.is-related {
    padding: 30px;
    border: 1px solid rgb(0, 0, 0, 0.12);

    @include responsive.mq_down(sp) {
      padding: 30px 20px;
    }

    .c-jyunkaiItem {
      &_component {
        padding: 0;

        &_image {
          margin-right: 30px;

          @include responsive.mq_down(sp) {
            margin-right: 0;
          }
        }

        &_data {
          flex: 1;
          width: 100%;
        }
      }
    }
  }
}

.aaa {
  color: #ff6347;
}
</style>
